import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';

import { LoggerService } from '@app/core/logging/logger.service';

/**
 * Puerto del documento: cómo cargó ESTA página y cómo traer una nueva. Las dos
 * cosas son el mismo recurso externo —el documento del navegador— y las dos son
 * imposibles de usar de verdad en un spec: `location.reload` no es espiable en
 * Chrome (la propiedad no es configurable) y una llamada real recargaría el
 * runner de Karma.
 */
export abstract class BrowserPage {
  /**
   * `true` solo si esta página es el RESULTADO de una recarga (F5 o
   * `location.reload()`). Es el freno anti-bucle que no depende de nada que se
   * pueda borrar: no vive en storage, así que ni un `sessionStorage.clear()` ni
   * un storage bloqueado lo apagan.
   */
  abstract loadedByReload(): boolean;

  /** Trae la página de nuevo, con la versión nueva del service worker si hay. */
  abstract reload(): void;
}

/** Tope para la activación del service worker antes de recargar. */
const TOPE_ACTIVACION_MS = 3000;

@Injectable({ providedIn: 'root' })
export class WindowBrowserPage extends BrowserPage {
  private readonly document = inject(DOCUMENT);
  private readonly swUpdate = inject(SwUpdate);
  private readonly logger = inject(LoggerService);

  loadedByReload(): boolean {
    // Sin el dato se responde `false` (o sea "no consta que sea una recarga") y
    // el freno pasa a ser la marca verificada, que sí es demostrable. Mentir
    // aquí a `true` apagaría la cura en ese navegador.
    return this.tipoDeCarga() === 'reload';
  }

  reload(): void {
    if (!this.swUpdate.isEnabled) {
      this.recargarDocumento();
      return;
    }

    // Con el service worker activo (production y staging) una recarga pelada se
    // puede servir de la versión CACHEADA: el chunk volvería a faltar y la cura
    // sería un no-op justo donde se reportó el defecto. Se activa antes la
    // versión nueva que ngsw ya tenga descargada (`AppUpdateService` consulta
    // cada 2 minutos, así que en una pestaña abierta durante un despliegue el
    // chequeo ya ocurrió).
    //
    // La carrera contra el tope existe porque la activación pasa por red: sin
    // ella, un CDN lento dejaría la página muerta sin recargar y sin aviso.
    const activacion = this.swUpdate.activateUpdate().catch(() => false);
    const tope = new Promise<boolean>((resolve) => setTimeout(() => resolve(false), TOPE_ACTIVACION_MS));

    void Promise.race([activacion, tope]).then(() => this.recargarDocumento());
  }

  private recargarDocumento(): void {
    this.document.location.reload();
  }

  /** `'reload' | 'navigate' | 'back_forward' | 'prerender'`, o `null` si el navegador no lo informa. */
  private tipoDeCarga(): string | null {
    try {
      const entradas = this.document.defaultView?.performance?.getEntriesByType('navigation') ?? [];
      const primera: unknown = entradas[0];
      const propiedades = typeof primera === 'object' && primera !== null ? (primera as Record<string, unknown>) : null;
      const tipo = propiedades?.['type'];
      return typeof tipo === 'string' ? tipo : null;
    } catch {
      // Navegadores viejos o con la API de rendimiento restringida.
      this.logger.warn('[chunk] el navegador no informa el tipo de carga de la página');
      return null;
    }
  }
}
